import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Redirect } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";

/* ---------- Tokens (mirrors FineSettings family) ---------- */

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const ui = {
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  ring: "rgba(3, 169, 244, 0.18)",
  success: "#10b981",
  warn: "#f59e0b",
  info: "#0ea5e9",
  radiusLg: 16,
  radiusMd: 12,
  shadowLift:
    "0 1px 2px rgba(15, 23, 42, 0.05), 0 16px 48px rgba(15, 23, 42, 0.10)",
  heroGrad: (c) =>
    `linear-gradient(135deg, ${c.primary_color_shade8} 0%, ${c.primary_color_shade6} 100%)`,
};

const STATUS_PALETTE = {
  active: {
    bg: "rgba(16, 185, 129, 0.14)",
    color: "#047857",
    dot: "#10b981",
  },
  inactive: {
    bg: "rgba(148, 163, 184, 0.20)",
    color: "#475569",
    dot: "#94a3b8",
  },
};

/* ---------- Scoped CSS ---------- */

const MODAL_CSS = `
.mss-modal-root :is(.form-control, input, select, textarea) {
  border-radius: 10px;
  border: 1px solid ${ui.border};
  background: ${ui.surface};
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${ui.text};
  transition: border-color .15s ease, box-shadow .15s ease;
}
.mss-modal-root :is(.form-control, input, select, textarea):focus {
  border-color: ${COLORS.primary_color_shade5};
  box-shadow: 0 0 0 4px ${ui.ring};
  outline: none;
}
.mss-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 1050;
  padding: 24px 16px 16px;
  overflow-y: auto;
  animation: mss-fade-in .15s ease-out;
}
@keyframes mss-fade-in { from { opacity: 0; } to { opacity: 1; } }
.mss-modal {
  width: 100%;
  max-width: 760px;
  background: ${ui.surface};
  border-radius: ${ui.radiusLg}px;
  border: 1px solid ${ui.border};
  box-shadow: ${ui.shadowLift};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px);
  margin-top: 0;
  font-family: ${fontStack};
  color: ${ui.text};
  animation: mss-rise .18s ease-out;
}
@keyframes mss-rise {
  from { transform: translateY(-10px) scale(0.99); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}
.mss-header {
  padding: 14px 18px;
  color: #fff;
  background: ${ui.heroGrad(COLORS)};
  position: relative;
}
.mss-header-orb {
  position: absolute; right: -60px; top: -60px;
  width: 200px; height: 200px; border-radius: 50%;
  background: rgba(255,255,255,0.08); pointer-events: none;
}
.mss-close {
  position: absolute; top: 12px; right: 12px;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff; cursor: pointer;
  backdrop-filter: blur(6px);
  font-size: 11px;
}
.mss-close:hover { background: rgba(255,255,255,0.28); }
.mss-body { padding: 16px; overflow: auto; }
.mss-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
@media (max-width: 767.98px) { .mss-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 479.98px) { .mss-grid { grid-template-columns: 1fr; } }
.mss-card {
  position: relative;
  background: ${ui.surface};
  border: 1px solid ${ui.border};
  border-radius: ${ui.radiusMd}px;
  padding: 12px 14px;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease, background .15s ease;
  display: flex; flex-direction: column; gap: 6px;
  min-height: 96px;
}
.mss-card:hover {
  transform: translateY(-2px);
  box-shadow: ${ui.shadowLift};
  border-color: ${COLORS.primary_color_shade5};
}
.mss-card-selected {
  border-color: ${COLORS.primary_color_shade5} !important;
  box-shadow: 0 0 0 4px ${ui.ring}, ${ui.shadowLift} !important;
  background: linear-gradient(135deg, rgba(3, 169, 244, 0.06), rgba(3, 169, 244, 0.02));
}
.mss-card-name {
  font-weight: 800; font-size: 15px; color: ${ui.text};
  display: flex; align-items: center; gap: 6px;
  word-break: break-word;
}
.mss-card-dates {
  font-size: 11.5px; color: ${ui.muted}; font-weight: 600;
  display: inline-flex; align-items: center; gap: 4px;
  font-variant-numeric: tabular-nums;
}
.mss-card-badge-row { display: flex; gap: 6px; flex-wrap: wrap; }
.mss-status-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-weight: 800; font-size: 10px;
  padding: 3px 8px; border-radius: 999px;
  letter-spacing: 0.06em; text-transform: uppercase;
}
.mss-check {
  position: absolute; top: 8px; right: 8px;
  color: ${COLORS.primary_color_shade6};
  font-size: 16px;
}
.mss-footer {
  padding: 12px 18px;
  border-top: 1px solid ${ui.divider};
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  background: ${ui.surfaceAlt};
  flex-wrap: wrap;
}
.mss-footer-info {
  font-size: 12px; color: ${ui.muted}; font-weight: 600;
  display: inline-flex; align-items: center; gap: 6px;
}
.mss-footer-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.mss-btn-ghost {
  background: ${ui.surface};
  border: 1px solid ${ui.border};
  color: ${ui.textSoft};
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.mss-btn-ghost:hover:not(:disabled) { background: ${ui.surfaceAlt}; }
.mss-btn-ghost:disabled { opacity: 0.55; cursor: not-allowed; }
.mss-btn-cta {
  background-image: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%) !important;
  background-color: ${COLORS.primary_color_shade5} !important;
  box-shadow: 0 6px 18px rgba(3, 169, 244, 0.35) !important;
  color: #fff !important;
  border: 1px solid ${COLORS.primary_color_shade7} !important;
  border-radius: 999px;
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  transition: filter .15s ease, transform .15s ease;
}
.mss-btn-cta:hover:not(:disabled) { filter: brightness(1.04); transform: translateY(-1px); }
.mss-btn-cta:disabled { opacity: 0.6; cursor: not-allowed; }
.mss-skeleton {
  position: relative; overflow: hidden;
  background: rgba(148, 163, 184, 0.18);
  border-radius: 8px;
}
.mss-skeleton::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
  animation: mss-shimmer 1.2s infinite;
}
@keyframes mss-shimmer { 100% { transform: translateX(100%); } }
`;

/* ---------- Helpers ---------- */

function toIsoDate(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  return s.length >= 10 ? s.substring(0, 10) : s;
}

function isActiveSession(item) {
  const raw = item?.status;
  if (typeof raw === "string") {
    const t = raw.trim().toLowerCase();
    // Treat "current" / "running" / "ongoing" the same as "active" so they
    // get the green status chip.
    return (
      raw === "1" ||
      t === "active" ||
      t === "current" ||
      t === "running" ||
      t === "ongoing"
    );
  }
  return !!raw;
}

/** Display label for the session status chip. Prefers the raw status text
 *  coming from the backend (e.g. "Closed", "Archived", "Upcoming") and falls
 *  back to Active/Inactive when only a boolean / 1 / 0 is provided. */
function statusLabel(item) {
  const raw = item?.status;
  if (typeof raw === "string" && raw.trim()) {
    const t = raw.trim();
    if (t === "1") return "Active";
    if (t === "0") return "Inactive";
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  return isActiveSession(item) ? "Active" : "Inactive";
}

function SkeletonGrid({ count = 6 }) {
  return (
    <div className="mss-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="mss-card"
          style={{ cursor: "default" }}
          aria-hidden
        >
          <div className="mss-skeleton" style={{ height: 16, width: "70%" }} />
          <div
            className="mss-skeleton"
            style={{ height: 11, width: "85%", marginTop: 4 }}
          />
          <div
            className="mss-skeleton"
            style={{ height: 16, width: 56, borderRadius: 999, marginTop: 6 }}
          />
        </div>
      ))}
    </div>
  );
}

/* ---------- Main component ---------- */

/** When stacked=true, renders above Bootstrap modals (e.g. report card picker). */
const STACKED_Z_INDEX = 1100;

const MdlSelectSession = ({ action, action2, stacked = false }) => {
  const [loggedIn] = useState(() => !!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  // Stale-request guard
  const reqIdRef = useRef(0);
  // Track current session (from SessionManager) so we can highlight it.
  const currentSessionIdRef = useRef("");

  /* Preselect current session id from SessionManager. */
  useEffect(() => {
    try {
      const cur =
        SessionManager.getCurrentSession && SessionManager.getCurrentSession();
      if (Array.isArray(cur) && cur[0]?.session_id) {
        currentSessionIdRef.current = String(cur[0].session_id);
      }
    } catch (_e) {
      /* non-fatal */
    }
  }, []);

  const closeDialog = useCallback(() => {
    if (typeof action === "function") action();
  }, [action]);

  /* Close on Escape. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeDialog();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeDialog]);

  /* Fetch session list on mount. */
  useEffect(() => {
    const fetchSessions = async () => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      try {
        const SCHOOL_CODE =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const FUNCTION_NAME = "getSessionList.php";
        const url =
          (process.env.REACT_APP_SERVER_NAME || "") +
          (process.env.REACT_APP_TARGET_DIR || "") +
          FUNCTION_NAME;
        const params = { school_code: SCHOOL_CODE };
        const qs = new URLSearchParams(
          Object.fromEntries(
            Object.entries(params).map(([k, v]) => [
              k,
              v != null ? String(v) : "",
            ])
          )
        ).toString();
        console.log("getSessionList full URL:", `${url}?${qs}`);

        const { data: response } = await axios.get(url, { params });
        if (reqId !== reqIdRef.current) return;

        if (response?.api_status === "OK" && Array.isArray(response?.data)) {
          setSessions(response.data);
          // Preselect the current session if present in the list.
          const curId = currentSessionIdRef.current;
          if (curId) {
            const idx = response.data.findIndex(
              (s) => String(s.session_id) === curId
            );
            setSelectedIndex(idx >= 0 ? idx : 0);
          } else {
            setSelectedIndex(0);
          }
        } else {
          setSessions([]);
          setSelectedIndex(-1);
          myToast.notify_warning(
            response?.api_remark || "No sessions available"
          );
        }
      } catch (e) {
        if (reqId !== reqIdRef.current) return;
        setSessions([]);
        setSelectedIndex(-1);
        myToast.notify_error(
          "Error loading sessions: " + (e?.message || "Unknown error")
        );
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    };
    fetchSessions();
    return () => {
      reqIdRef.current = -1;
    };
  }, []);

  const handleSelect = useCallback((index) => {
    setSelectedIndex(index);
  }, []);

  //Created By Cursor — confirm by explicit index (avoids stale selectedIndex on dbl-click)
  const confirmAtIndex = useCallback(
    (index) => {
      if (index < 0 || index >= sessions.length) {
        myToast.notify_warning("Please select a session first");
        return;
      }
      const s = sessions[index];
      if (typeof action2 === "function") {
        action2(
          s.session_id,
          s.session_name,
          s.status,
          s.start_date,
          s.end_date
        );
      }
    },
    [sessions, action2]
  );

  const handleConfirm = useCallback(() => {
    confirmAtIndex(selectedIndex);
  }, [confirmAtIndex, selectedIndex]);

  /* Backdrop click closes (clicks inside the modal box don't propagate). */
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) closeDialog();
    },
    [closeDialog]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const modalTree = (
    <div className="mss-modal-root">
      <style>{MODAL_CSS}</style>
      <div
        className="mss-modal-backdrop"
        role="presentation"
        onClick={handleBackdropClick}
        style={stacked ? { zIndex: STACKED_Z_INDEX } : undefined}
      >
        <div
          className="mss-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mss-title"
        >
          {/* Header */}
          <div className="mss-header">
            <span className="mss-header-orb" aria-hidden />
            <button
              type="button"
              className="mss-close"
              onClick={closeDialog}
              aria-label="Close"
              title="Close (Esc)"
            >
              <FaTimes aria-hidden />
            </button>
            <div
              id="mss-title"
              style={{
                fontWeight: 800,
                fontSize: 17,
                paddingRight: 32,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FaCalendarAlt aria-hidden style={{ fontSize: 15 }} />
              Select Session
            </div>
            <div
              style={{
                fontSize: 12,
                opacity: 0.9,
                marginTop: 2,
                paddingRight: 32,
              }}
            >
              Pick the academic session you want to work with.
            </div>
          </div>

          {/* Body */}
          <div className="mss-body">
            {loading ? (
              <SkeletonGrid count={6} />
            ) : sessions.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px 16px",
                  color: ui.muted,
                  fontSize: 14,
                }}
              >
                <div
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                    marginBottom: 10,
                  }}
                  aria-hidden
                >
                  <FaCalendarAlt style={{ fontSize: 22 }} />
                </div>
                <div
                  style={{
                    fontWeight: 800,
                    color: ui.text,
                    fontSize: 15,
                    marginBottom: 4,
                  }}
                >
                  No sessions available
                </div>
                <div>Add a session from the admin panel and try again.</div>
              </div>
            ) : (
              <div className="mss-grid">
                {sessions.map((item, index) => {
                  const isSelected = selectedIndex === index;
                  const isCurrent =
                    String(item.session_id) === currentSessionIdRef.current;
                  const active = isActiveSession(item);
                  const palette = active
                    ? STATUS_PALETTE.active
                    : STATUS_PALETTE.inactive;
                  const start = toIsoDate(item.start_date);
                  const end = toIsoDate(item.end_date);
                  return (
                    <div
                      key={item.session_id || index}
                      className={`mss-card ${
                        isSelected ? "mss-card-selected" : ""
                      }`}
                      onClick={() => handleSelect(index)}
                      onDoubleClick={() => {
                        handleSelect(index);
                        confirmAtIndex(index);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelect(index);
                        }
                      }}
                      aria-pressed={isSelected}
                      title={
                        isCurrent ? "Currently active session" : "Select"
                      }
                    >
                      {isSelected ? (
                        <FaCheckCircle
                          className="mss-check"
                          aria-label="Selected"
                        />
                      ) : null}
                      <div className="mss-card-name">
                        {item.session_name || `Session ${item.session_id}`}
                      </div>
                      {start || end ? (
                        <div className="mss-card-dates">
                          <FaCalendarAlt
                            style={{ fontSize: 10, opacity: 0.7 }}
                            aria-hidden
                          />
                          {start || "—"} → {end || "—"}
                        </div>
                      ) : null}
                      <div className="mss-card-badge-row">
                        <span
                          className="mss-status-chip"
                          style={{
                            background: palette.bg,
                            color: palette.color,
                          }}
                        >
                          <span
                            aria-hidden
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: palette.dot,
                            }}
                          />
                          {statusLabel(item)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mss-footer">
            <div className="mss-footer-info">
              <FaInfoCircle
                style={{ color: ui.info }}
                aria-hidden
              />
              {selectedIndex >= 0 && sessions[selectedIndex] ? (
                <>
                  Selected:{" "}
                  <strong style={{ color: ui.text }}>
                    {sessions[selectedIndex].session_name}
                  </strong>
                </>
              ) : (
                "Pick a session above"
              )}
            </div>
            <div className="mss-footer-actions">
              <button
                type="button"
                className="mss-btn-ghost"
                onClick={closeDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mss-btn-cta"
                onClick={handleConfirm}
                disabled={loading || selectedIndex < 0}
              >
                <FaCheckCircle aria-hidden /> Use this session
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (stacked && typeof document !== "undefined") {
    return createPortal(modalTree, document.body);
  }

  return modalTree;
};

export default MdlSelectSession;
